import { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { GraduationCap, Check, Loader2, Trash2, Download, ExternalLink, BookOpen } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LMSSection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showMoodleForm, setShowMoodleForm] = useState(false);
  const [moodleUrl, setMoodleUrl] = useState('');
  const [moodleToken, setMoodleToken] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [importing, setImporting] = useState(null);
  const [activeProvider, setActiveProvider] = useState(null);

  useEffect(() => {
    fetchStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/lms/status`);
      setStatus(res.data);
    } catch { /* silent */ }
    setLoading(false);
  };

  const connectMoodle = async () => {
    if (!moodleUrl.trim() || !moodleToken.trim()) {
      toast.error('Enter both site URL and token');
      return;
    }
    setConnecting(true);
    try {
      const res = await axios.post(`${API}/lms/moodle/connect`, {
        site_url: moodleUrl, token: moodleToken
      });
      toast.success(res.data.message);
      setShowMoodleForm(false);
      fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect');
    }
    setConnecting(false);
  };

  const connectClassroom = async () => {
    try {
      const res = await axios.get(`${API}/lms/google-classroom/connect`);
      if (res.data.authorization_url) window.location.href = res.data.authorization_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to connect');
    }
  };

  const disconnect = async (provider) => {
    if (!window.confirm('Disconnect this LMS? Imported courses will remain.')) return;
    try {
      if (provider === 'moodle') await axios.post(`${API}/lms/moodle/disconnect`);
      else await axios.post(`${API}/lms/google-classroom/disconnect`);
      toast.success('Disconnected');
      fetchStatus();
      setCourses([]);
    } catch {
      toast.error('Failed to disconnect');
    }
  };

  const loadCourses = async (provider) => {
    setActiveProvider(provider);
    setCoursesLoading(true);
    try {
      const endpoint = provider === 'moodle' ? '/lms/moodle/courses' : '/lms/google-classroom/courses';
      const res = await axios.get(`${API}${endpoint}`);
      setCourses(res.data.courses || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load courses');
      setCourses([]);
    }
    setCoursesLoading(false);
  };

  const importCourse = async (course) => {
    setImporting(course.lms_id);
    try {
      const endpoint = course.provider === 'moodle' ? '/lms/moodle/import-course' : '/lms/google-classroom/import-course';
      const res = await axios.post(`${API}${endpoint}?course_id=${course.lms_id}`);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    }
    setImporting(null);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.6 }}>
      <Card className="p-6  border-2" data-testid="lms-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 rounded-xl bg-purple-500/10">
            <GraduationCap className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-[var(--isp-heading)]">University LMS</h3>
            <p className="text-xs text-[var(--isp-muted)]">Import courses from Moodle or Google Classroom</p>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-purple-500" /></div>
        ) : (
          <div className="space-y-4">
            {/* Moodle */}
            <div className="p-4 rounded-xl border border-[var(--isp-glass-border)] bg-[var(--isp-glass-inner)]" data-testid="moodle-section">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 rounded-lg bg-orange-500/10 flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-orange-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[var(--isp-heading)]">Moodle</p>
                  <p className="text-[10px] text-[var(--isp-muted)]">
                    {status?.moodle?.connected ? `Connected · ${status.moodle.username || status.moodle.site_url}` : 'Connect your university Moodle'}
                  </p>
                </div>
                {status?.moodle?.connected ? (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => loadCourses('moodle')} className="rounded-lg text-xs gap-1 bg-orange-500 hover:bg-orange-600" data-testid="moodle-browse-btn">
                      <Download className="w-3 h-3" /> Import Courses
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => disconnect('moodle')} className="text-red-400 hover:text-red-500" data-testid="moodle-disconnect-btn">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" onClick={() => setShowMoodleForm(!showMoodleForm)} className="rounded-lg text-xs bg-orange-500 hover:bg-orange-600" data-testid="moodle-connect-btn">
                    Connect
                  </Button>
                )}
              </div>

              <AnimatePresence>
                {showMoodleForm && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }} className="overflow-hidden">
                    <div className="space-y-2 pt-3 border-t border-[var(--isp-divider)] mt-2">
                      <Input value={moodleUrl} onChange={e => setMoodleUrl(e.target.value)} placeholder="Moodle URL (e.g. https://moodle.ucc.edu.gh)"
                        className="rounded-lg text-sm bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="moodle-url-input" />
                      <Input value={moodleToken} onChange={e => setMoodleToken(e.target.value)} placeholder="Web Service Token" type="password"
                        className="rounded-lg text-sm bg-[var(--isp-glass)] border-[var(--isp-glass-border)]" data-testid="moodle-token-input" />
                      <p className="text-[10px] text-[var(--isp-muted)]">
                        Get your token from: Moodle → Preferences → Security Keys → Create Token
                      </p>
                      <Button onClick={connectMoodle} disabled={connecting} className="rounded-lg text-xs w-full bg-orange-500 hover:bg-orange-600" data-testid="moodle-submit-btn">
                        {connecting ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Connect Moodle'}
                      </Button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Google Classroom */}
            <div className="p-4 rounded-xl border border-[var(--isp-glass-border)] bg-[var(--isp-glass-inner)]" data-testid="classroom-section">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-green-500/10 flex items-center justify-center">
                  <GraduationCap className="w-4 h-4 text-green-500" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-[var(--isp-heading)]">Google Classroom</p>
                  <p className="text-[10px] text-[var(--isp-muted)]">
                    {status?.google_classroom?.connected ? `Connected · ${status.google_classroom.email}` : status?.google_classroom?.available ? 'Click to connect' : 'Setup required'}
                  </p>
                </div>
                {status?.google_classroom?.connected ? (
                  <div className="flex gap-2">
                    <Button size="sm" onClick={() => loadCourses('google_classroom')} className="rounded-lg text-xs gap-1 bg-green-500 hover:bg-green-600" data-testid="classroom-browse-btn">
                      <Download className="w-3 h-3" /> Import Courses
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => disconnect('google_classroom')} className="text-red-400 hover:text-red-500" data-testid="classroom-disconnect-btn">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                ) : (
                  <Button size="sm" onClick={connectClassroom} disabled={!status?.google_classroom?.available}
                    className="rounded-lg text-xs bg-green-500 hover:bg-green-600" data-testid="classroom-connect-btn">
                    Connect
                  </Button>
                )}
              </div>
            </div>

            {/* Courses List */}
            <AnimatePresence>
              {(courses.length > 0 || coursesLoading) && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <div className="p-4 rounded-xl border border-[var(--isp-glass-border)]" data-testid="lms-courses-list">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-sm font-semibold text-[var(--isp-heading)]">
                        {activeProvider === 'moodle' ? 'Moodle' : 'Classroom'} Courses
                      </p>
                      <button onClick={() => setCourses([])} className="text-[10px] text-[var(--isp-muted)] hover:text-[var(--isp-heading)]">Close</button>
                    </div>
                    {coursesLoading ? (
                      <div className="text-center py-4"><Loader2 className="w-5 h-5 animate-spin mx-auto text-purple-500" /></div>
                    ) : courses.length === 0 ? (
                      <p className="text-sm text-[var(--isp-muted)] text-center py-4">No courses found</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {courses.map(c => (
                          <div key={c.lms_id} className="flex items-center gap-3 p-3 rounded-lg bg-[var(--isp-glass-inner)] hover:bg-white/5" data-testid={`course-${c.lms_id}`}>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-[var(--isp-heading)] truncate">{c.name}</p>
                              {c.short_name && <p className="text-[10px] text-[var(--isp-muted)]">{c.short_name}</p>}
                            </div>
                            <Button size="sm" onClick={() => importCourse(c)} disabled={importing === c.lms_id}
                              className="rounded-lg text-xs gap-1 bg-[#002FA7] hover:bg-[#00227A]" data-testid={`import-${c.lms_id}`}>
                              {importing === c.lms_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
                              Import
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <p className="text-[10px] text-[var(--isp-muted)] leading-relaxed pt-2 border-t border-[var(--isp-divider)]">
              Imported courses become subjects with their resources as lectures. You can record, transcribe, and study them with AI.
            </p>
          </div>
        )}
      </Card>
    </motion.div>
  );
};

export default LMSSection;
